import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {makeStyles} from "@mui/styles";
import axios from "axios";
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import {API_ENDPOINT, COLOR_BRAND} from "../consts";

const useStyles = makeStyles((theme) => ({
    container: {
        flex: 1
    },
    innerContainer: {
        display: "flex",
        width: "100%"
    },
    box: {
        width: "100%",
        maxWidth: 800,
        padding: 20,
        backgroundColor: "#fff",
        webkitBoxShadow: "1px 2px 3px #00000033",
        mozBoxShadow: "1px 2px 3px #00000033",
        boxShadow: "1px 2px 3px #00000033",
        borderRadius: 5
    },
    uploadControls: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginTop: 8,
        marginBottom: 16
    },
    placeholder: {
        border: '2px dashed #cfd8dc',
        background: '#fafafa',
        borderRadius: 6,
        padding: 16,
        textAlign: 'center',
        color: '#607d8b'
    },
    sectionTitle: {
        marginTop: 24,
        marginBottom: 8
    },
    progressBox: {
        padding: 16,
        backgroundColor: '#fafafa',
        borderRadius: 6,
        border: '1px solid #e0e0e0'
    }
}));

export function ImportScreen() {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [language, setLanguage] = useState('en');
    const [importProgress, setImportProgress] = useState(null); // { id, filename, currentCount, maxCount, createdAt, updatedAt, error }
    const pollIntervalRef = useRef(null);
    const classes = useStyles();

    const onFileChange = (e) => {
        const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
        setFile(f);
    };

    const fetchProgress = useCallback(async () => {
        try {
            const res = await axios.get(API_ENDPOINT + '?func=import_progress');
            if (res.status === 200 && res.data && res.data.error === false) {
                setImportProgress(res.data.progress);
            }
        } catch (e) {
            // Non-fatal; keep silent, user can retry
        }
    }, []);

    useEffect(() => {
        // Initial load
        fetchProgress();
    }, [fetchProgress]);

    useEffect(() => {
        // While uploading, poll more frequently
        if (uploading) {
            pollIntervalRef.current = setInterval(fetchProgress, 2000);
        } else if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [uploading, fetchProgress]);

    const percentDone = useMemo(() => {
        if (!importProgress || !importProgress.maxCount || importProgress.maxCount <= 0) return null;
        const v = Math.floor((Number(importProgress.currentCount || 0) / Number(importProgress.maxCount)) * 100);
        return Math.max(0, Math.min(100, v));
    }, [importProgress]);

    const onUpload = async () => {
        if (!file) return;
        try {
            setUploading(true);
            setProgress(0);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('lang', language);
            // Backend should handle parsing and queuing import
            const url = API_ENDPOINT + `?func=import_products&lang=${encodeURIComponent(language)}`;
            let res = await axios.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (evt) => {
                    if (!evt.total) return;
                    setProgress(Math.floor((evt.loaded / evt.total) * 100));
                }
            });

            if (res.status !== 200 || !res.data || res.data.error === true) {
                console.log(res.data.count);
                throw new Error('Upload failed');
            }

            setSnackbarSeverity('success');
            setSnackbarMsg('Imported ' + res.data.count + ' products successfully.');
            setSnackbarOpen(true);
            //setFile(null);
            // Immediately refresh server-side progress after upload triggers
            fetchProgress();
        } catch (err) {
            setSnackbarSeverity('error');
            setSnackbarMsg('Upload failed. Please check the file and try again.');
            setSnackbarOpen(true);
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    return (
        <>
            <div className={classes.container}>
                <h1>Import products</h1>
                <div className={classes.innerContainer}>
                    <div className={classes.box}>
                        <Typography variant="h6" gutterBottom>Upload Excel file</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Accepted formats: .xlsx, .xls (CSV optional)
                        </Typography>
                        <div className={classes.uploadControls}>
                            <FormControl size="small" sx={{minWidth: 160}}>
                                <InputLabel id="import-language-label">Language</InputLabel>
                                <Select
                                    labelId="import-language-label"
                                    id="import-language"
                                    label="Language"
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                >
                                    <MenuItem value={'cs'}>Czech (cs)</MenuItem>
                                    <MenuItem value={'sk'}>Slovak (sk)</MenuItem>
                                    <MenuItem value={'sl'}>Slovene (sl)</MenuItem>
                                    <MenuItem value={'en'}>English (en)</MenuItem>
                                </Select>
                            </FormControl>
                            <input
                                id="excel-file"
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={onFileChange}
                            />
                            <Button
                                variant="contained"
                                style={{backgroundColor: COLOR_BRAND}}
                                disabled={!file || uploading}
                                onClick={onUpload}
                            >
                                {uploading ? 'Uploading…' : 'Upload'}
                            </Button>
                        </div>
                        {uploading && (
                            <Box sx={{width: '100%', mb: 2}}>
                                <LinearProgress variant="indeterminate" value={progress}/>
                                <Typography variant="caption">{progress}%</Typography>
                            </Box>
                        )}

                        <Typography className={classes.sectionTitle} variant="subtitle1">Import progress</Typography>
                        <Box className={classes.progressBox}>
                            <Stack spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                    <Typography variant="body2" color="textSecondary">Latest status</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Button size="small" variant="outlined" onClick={fetchProgress}>Refresh</Button>
                                    </Stack>
                                </Stack>
                                {importProgress ? (
                                    <>
                                        <Typography variant="body1">
                                            File: <strong>{importProgress.filename || 'n/a'}</strong>
                                        </Typography>
                                        <Typography variant="body2">
                                            Items: <strong>{importProgress.currentCount ?? 0}</strong> / {importProgress.maxCount ?? 0}
                                        </Typography>
                                        {percentDone !== null && (
                                            <>
                                                <LinearProgress variant="determinate" value={percentDone}/>
                                                <Typography variant="caption">{percentDone}%</Typography>
                                            </>
                                        )}
                                        <Divider sx={{my: 1}}/>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Chip size="small" label={`Updated: ${importProgress.updatedAt ? new Date(importProgress.updatedAt).toLocaleString() : 'n/a'}`}/>
                                            <Chip size="small" color="default" label={`Started: ${importProgress.createdAt ? new Date(importProgress.createdAt).toLocaleString() : 'n/a'}`}/>
                                        </Stack>
                                        {importProgress.error && (
                                            <Alert severity="error" sx={{mt: 1}}>
                                                {importProgress.error}
                                            </Alert>
                                        )}
                                    </>
                                ) : (
                                    <Typography variant="body2" color="textSecondary">No recent import found.</Typography>
                                )}
                            </Stack>
                        </Box>

                        <Typography variant="subtitle1" gutterBottom>Expected Header Structure</Typography>
                        <Paper className={classes.placeholder} elevation={0}>
                            {/* Replace with real screenshot at public/import-header.png */}
                            <img
                                src={process.env.PUBLIC_URL + '/import-header.png'}
                                alt="Excel header example"
                                style={{maxWidth: '100%', display: 'inline-block'}}
                            />
                        </Paper>
                    </div>
                </div>
            </div>
            <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMsg}
                </Alert>
            </Snackbar>
        </>
    );

}
